import { useState, useEffect } from 'react';
import type { FormEvent, ChangeEvent } from 'react';
import { TrashIcon, PencilIcon, LockClosedIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService } from '../utils/api';

type PrivilegeKey = 'manage_courses' | 'manage_students' | 'manage_content';

interface Moderator {
  id: string;
  name: string;
  email: string;
  status: 'active' | 'inactive';
  privileges: PrivilegeKey[];
  createdAt: string;
}

interface FormData {
  name: string;
  email: string;
  password: string;
  privileges: Record<PrivilegeKey, boolean>;
}

const SystemUsers = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingModerator, setEditingModerator] = useState<Moderator | null>(null);
  const [moderatorToDelete, setModeratorToDelete] = useState<Moderator | null>(null);
  const [moderatorToToggle, setModeratorToToggle] = useState<Moderator | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moderators, setModerators] = useState<Moderator[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filteredModerators, setFilteredModerators] = useState<Moderator[]>([]);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    privileges: {
      manage_courses: false,
      manage_students: false,
      manage_content: false,
    },
  });

  // Add success message state
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchModerators();
  }, [currentPage, pageSize]);

  useEffect(() => {
    // Apply local search filter whenever moderators or searchTerm changes
    const filtered = moderators.filter(moderator => 
      moderator.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      moderator.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredModerators(filtered);
    // Update total count and pages for pagination
    setTotalCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  }, [moderators, searchTerm, pageSize]);

  const fetchModerators = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authFetch(apiUrl(`ModeratorAuth/get-all?pagenumber=${currentPage}&pagesize=${pageSize}`));

      if (!response.ok) {
        throw new Error('Failed to fetch moderators');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch moderators');
      }

      // Map the API response to our Moderator interface
      const mappedModerators: Moderator[] = result.data.map((mod: any) => ({
        id: mod.id,
        name: mod.username,
        email: mod.email,
        status: mod.active ? 'active' : 'inactive',
        privileges: mod.privileges.map((privilege: string) => {
          // Convert API privilege format to our local format
          switch(privilege) {
            case 'ManageCourse':
              return 'manage_courses';
            case 'ManageStudent':
              return 'manage_students';
            case 'ManageContent':
              return 'manage_content';
            default:
              return privilege.toLowerCase() as PrivilegeKey;
          }
        }),
        createdAt: mod.createdAt
      }));

      setModerators(mappedModerators);
      setFilteredModerators(mappedModerators);
      // Since the API doesn't return total count and pages, we'll calculate based on the data
      setTotalCount(mappedModerators.length);
      setTotalPages(Math.ceil(mappedModerators.length / pageSize));
    } catch (error) {
      console.error('Error fetching moderators:', error);
      setError('Failed to load moderators. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    const selectedPrivileges = Object.entries(formData.privileges)
      .filter(([_, value]) => value)
      .map(([key]) => {
        // Convert privilege keys to proper enum values
        switch(key) {
          case 'manage_courses':
            return 'ManageCourse';
          case 'manage_students':
            return 'ManageStudent';
          case 'manage_content':
            return 'ManageContent';
          default:
            return key;
        }
      });

    if (selectedPrivileges.length === 0) {
      setError('Please select at least one privilege');
      return;
    }

    try {
      setIsLoading(true);

      if (editingModerator) {
        // Update existing moderator using API - without password
        const response = await authFetch(apiUrl(`ModeratorAuth/info/${editingModerator.id}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'accept': '*/*',
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            privileges: selectedPrivileges
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || 'Failed to update moderator');
        }

        if (result.success) {
          setSuccessMessage('Moderator updated successfully');
          setTimeout(() => {
            setSuccessMessage(null);
          }, 3000);
          
          // Refresh the moderators list
          await fetchModerators();
          
          // Close the edit form
          setShowEditForm(false);
          setEditingModerator(null);
        } else {
          throw new Error(result.message || 'Failed to update moderator');
        }
      } else {
        // Add new moderator using API - include password
        const response = await authFetch(apiUrl('ModeratorAuth/register'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'accept': '*/*',
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            password: formData.password,
            createdAt: new Date().toISOString(),
            privileges: selectedPrivileges
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          if (result.errors) {
            const errorMessages = Object.values(result.errors)
              .flat()
              .join(', ');
            throw new Error(errorMessages);
          }
          throw new Error(result.message || 'Failed to register moderator');
        }

        if (result.success) {
          setSuccessMessage('Moderator added successfully');
          setTimeout(() => {
            setSuccessMessage(null);
          }, 3000);
          
          // Refresh the moderators list
          await fetchModerators();
          
          // Close the add form
          setShowAddForm(false);
        } else {
          throw new Error(result.message || 'Failed to register moderator');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }

    // Reset form
    setFormData({
      name: '',
      email: '',
      password: '',
      privileges: {
        manage_courses: false,
        manage_students: false,
        manage_content: false,
      },
    });
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev: FormData) => {
      if (type === 'checkbox') {
        return {
          ...prev,
          privileges: {
            ...prev.privileges,
            [name]: checked,
          },
        };
      }
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleEditModerator = (moderator: Moderator) => {
    setEditingModerator(moderator);
    
    // Initialize form data with moderator data
    setFormData({
      name: moderator.name,
      email: moderator.email,
      password: '', // Clear password field when editing
      privileges: {
        manage_courses: moderator.privileges.includes('manage_courses'),
        manage_students: moderator.privileges.includes('manage_students'),
        manage_content: moderator.privileges.includes('manage_content'),
      },
    });
    
    setShowEditForm(true);
  };

  const handleDeleteModerator = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      const response = await authFetch(
        apiUrl(`ModeratorAuth/DElete/${id}?moderatorId=${id}`),
        {
          method: 'DELETE',
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to delete moderator');
      }

      if (result.success) {
        // Remove the moderator from local state
        setModerators(moderators.filter(mod => mod.id !== id));
        setModeratorToDelete(null);
        // Show success message
        setSuccessMessage(result.data || 'Moderator deleted successfully');
        // Hide success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
        // Refresh the list after deletion
        fetchModerators();
      } else {
        throw new Error(result.message || 'Failed to delete moderator');
      }
    } catch (error) {
      console.error('Error deleting moderator:', error);
      setError(error instanceof Error ? error.message : 'An error occurred while deleting the moderator');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = (id: string) => {
    setModerators(moderators.map(mod => 
      mod.id === id 
        ? { ...mod, status: mod.status === 'active' ? 'inactive' : 'active' }
        : mod
    ));
    setModeratorToToggle(null);
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    // The search is already applied through the useEffect above
    // This is just to handle the form submission
  };

  const privilegeLabels: Record<PrivilegeKey, string> = {
    manage_courses: 'Manage Courses',
    manage_students: 'Manage Students',
    manage_content: 'Manage Codes',
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchModerators();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-gray-900">System Users</h1>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Add New Moderator
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Search Form */}
        <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6 rounded-t-lg shadow-sm">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Search
            </button>
          </form>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-50 p-4 mt-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="rounded-md bg-green-50 p-4 mt-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Moderators Table */}
        <div className="mt-4">
          <div className="flex flex-col">
            <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Privileges
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {isLoading ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-4 whitespace-nowrap text-center">
                            Loading...
                          </td>
                        </tr>
                      ) : filteredModerators.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-4 whitespace-nowrap text-center">
                            No moderators found
                          </td>
                        </tr>
                      ) : (
                        filteredModerators.map((moderator) => (
                          <tr key={moderator.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{moderator.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">{moderator.email}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                moderator.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {moderator.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {moderator.privileges.map(privilege => (
                                <span key={privilege} className="mr-2 px-2 py-1 bg-gray-100 rounded-full text-xs">
                                  {privilege.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                </span>
                              ))}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => handleEditModerator(moderator)}
                                className="text-indigo-600 hover:text-indigo-900 mr-4"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => setModeratorToDelete(moderator)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Moderator Modal */}
      {(showAddForm || showEditForm) && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {showAddForm ? 'Add New Moderator' : 'Edit Moderator'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                {showAddForm && (
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <input
                      type="password"
                      name="password"
                      id="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Privileges
                  </label>
                  <div className="space-y-2">
                    {Object.entries(privilegeLabels).map(([key, label]) => (
                      <div key={key} className="flex items-center">
                        <input
                          type="checkbox"
                          id={key}
                          name={key}
                          checked={formData.privileges[key as PrivilegeKey]}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor={key} className="ml-2 block text-sm text-gray-900">
                          {label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setShowEditForm(false);
                    setEditingModerator(null);
                  }}
                  className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {isLoading ? 'Processing...' : showAddForm ? 'Add Moderator' : 'Update Moderator'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {moderatorToDelete && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Moderator</h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete {moderatorToDelete.name}? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setModeratorToDelete(null)}
                className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteModerator(moderatorToDelete.id)}
                disabled={isLoading}
                className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isLoading ? 'Processing...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Status Confirmation Modal */}
      {moderatorToToggle && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {moderatorToToggle.status === 'active' ? 'Deactivate' : 'Activate'} Moderator
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to {moderatorToToggle.status === 'active' ? 'deactivate' : 'activate'} {moderatorToToggle.name}?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setModeratorToToggle(null)}
                className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleToggleStatus(moderatorToToggle.id)}
                className={`py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  moderatorToToggle.status === 'active'
                    ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                    : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50`}
              >
                {moderatorToToggle.status === 'active' ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemUsers;